import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { s as slotFilled } from './index3.js';
import { p as parseHrefProp } from './index10.js';
import { g as getDataAttributes, s as splitElementAttributes } from './data-attributes.js';
import { d as defineCustomElement$j } from './box.js';
import { d as defineCustomElement$i } from './button.js';
import { d as defineCustomElement$h } from './check-input.js';
import { d as defineCustomElement$g } from './close-button.js';
import { d as defineCustomElement$f } from './divider.js';
import { d as defineCustomElement$e } from './form-field.js';
import { d as defineCustomElement$d } from './icon.js';
import { d as defineCustomElement$c } from './list.js';
import { d as defineCustomElement$b } from './list-item.js';
import { d as defineCustomElement$a } from './list-item-divider.js';
import { d as defineCustomElement$9 } from './list-item-header.js';
import { d as defineCustomElement$8 } from './loader.js';
import { d as defineCustomElement$7 } from './menu-pane.js';
import { d as defineCustomElement$6 } from './more-menu.js';
import { d as defineCustomElement$5 } from './status.js';
import { d as defineCustomElement$4 } from './tag.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const topNavCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host nav{align-content:center;align-items:center;background-color:var(--jw-theme-surface-elevated-default-bg-color);display:flex;flex:1 0 0;flex-wrap:wrap;gap:.75rem;justify-content:space-between;padding:.75rem 1.5rem}.user-menu-container{align-content:center;align-items:center;display:flex;gap:.125rem}.center-slot-container{flex-grow:1;max-width:var(--jw-container-md);min-width:var(--jw-container-xs);width:80%}.right-slot-container{align-items:center;display:flex;gap:.75rem;justify-content:center}";
const WuiTopNavStyle0 = topNavCss;

const WuiTopNav$1 = /*@__PURE__*/ proxyCustomElement(class WuiTopNav extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.menuOpenChanged = createEvent(this, "menuOpenChanged", 7);
        this.menuOpen = false;
        this.userMenu = undefined;
        this.statusIndicator = undefined;
    }
    toggleOpen(open = !this.menuOpen) {
        if (open === this.menuOpen) {
            return;
        }
        this.menuOpen = open;
        this.menuOpenChanged.emit(this.menuOpen);
    }
    getRightSideSlots() {
        return Array.from(this.el.querySelectorAll('[slot="right-side-content"]')).map((elem) => (h("span", { innerHTML: elem.outerHTML })));
    }
    getStatusIndicator() {
        if (!this.statusIndicator)
            return null;
        return h("wui-tag", { ...this.statusIndicator });
    }
    getUserMenu() {
        if (!this.userMenu) {
            return null;
        }
        const { signoutButton, warning, displayName, sections } = this.userMenu;
        return (h("div", { class: "user-menu-container" }, warning && h("wui-status", { intent: "warning", type: "icon", text: warning }), h("wui-more-menu", { buttonType: "ghost", label: displayName, icon: this.menuOpen ? 'chevron-up' : 'chevron-down', iconPosition: "trailing", disabled: sections.length === 0, closeOnClickInside: true, ...getDataAttributes(this.userMenu), menuPane: {
                placement: 'bottom-end',
                stretch: 'off',
            } }, h("wui-list", { class: "user-menu" }, sections.map((section) => (h(Fragment, null, section.title && (h("wui-list-item-header", { key: `section-title-${section.title}-${section.links.length}`, label: section.title })), section.links.map((link) => {
            const { dataAttributes, elementAttributes } = splitElementAttributes(link);
            return (h("wui-list-item", { key: `user-menu-${link.name}`, label: link.name, highlight: link.highlight, as: ['a', parseHrefProp(elementAttributes)], ...(typeof link !== 'string' ? dataAttributes : {}) }));
        }), h("wui-list-item-divider", null)))), h("wui-list-item", { onClick: signoutButton.onSignOut, label: signoutButton.label || 'Sign out', ...getDataAttributes(signoutButton) })))));
    }
    renderRightSideContent() {
        const slots = this.getRightSideSlots();
        const userMenu = this.getUserMenu();
        const statusIndicator = this.getStatusIndicator();
        const rightContentElements = [statusIndicator, ...slots, userMenu].filter(Boolean);
        return (h("section", { class: "right-slot-container" }, rightContentElements.map((element, idx) => {
            return (h(Fragment, null, element, idx !== rightContentElements.length - 1 && h("wui-divider", { type: "vertical" })));
        })));
    }
    render() {
        return (h(Host, { key: '6a3dac72a5c2fcf29b84fff1c7ae237b3dc75033' }, h("nav", { key: '848dd71f2ae49c5d19756d974ccd98cab369775b' }, h("section", { key: 'de7a5968eb3e587ed973f944d4ccc5c22d4a8176', class: "left-slot-container" }, h("slot", { key: 'd7eb578acd3d18b0900a603d9422cd8100a0a8ac', name: "left-side-content" })), slotFilled(this.el, 'center-content') && (h("section", { key: '61ed08ba5a76b3e7f95b18e554961a521525d073', class: "center-slot-container" }, h("slot", { key: '95f10de8cf24cc44341840750ad926865f528af6', name: "center-content" }))), this.renderRightSideContent())));
    }
    get el() { return this; }
    static get style() { return WuiTopNavStyle0; }
}, [1, "wui-top-nav", {
        "userMenu": [16],
        "statusIndicator": [16],
        "menuOpen": [32]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-top-nav", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-loader", "wui-menu-pane", "wui-more-menu", "wui-status", "wui-tag", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-top-nav":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiTopNav$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-more-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-tag":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiTopNav = WuiTopNav$1;
const defineCustomElement = defineCustomElement$1;

export { WuiTopNav, defineCustomElement };

//# sourceMappingURL=wui-top-nav.js.map